import React, { useId, useMemo, useState } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatUsdOrDash } from '../../utils/formatters';
import { VolumeDataPoint } from '../../features/processVolumeData';
import { ChartCard, ChartTooltipFrame, axisTick } from './ChartCard';
import { tokens } from '../../theme';

interface VolumeChartProps {
  data: VolumeDataPoint[];
  title: string;
  subtitle?: React.ReactNode;
  timeframe?: 'daily' | 'weekly' | 'monthly';
  /** Header control (e.g. timeframe switch). */
  action?: React.ReactNode;
}

/** Not a ledger: the shared card rule (formatUsd); zero reads "—". */
const usd = formatUsdOrDash;

/** Round steps only: 1, 2, 2.5 or 5 × 10ⁿ. */
const NICE_STEPS = [1, 2, 2.5, 5];

/**
 * Y ticks from 0 on a round step, with 3–5 intervals: of the steps that fit, the one whose last tick
 * leaves the least room above the tallest bar (fewer ticks on a tie). The axis domain ends on the last
 * tick, so every gridline carries a round label ($0 · $250K · … · $1.25M, never $1.05M).
 */
const volumeTicks = (max: number): number[] | undefined => {
  if (!Number.isFinite(max) || max <= 0) return undefined;
  const magnitude = Math.floor(Math.log10(max));
  let best: { step: number; count: number; top: number } | undefined;
  for (let exp = magnitude - 2; exp <= magnitude; exp++) {
    for (const m of NICE_STEPS) {
      const step = m * 10 ** exp;
      // Tolerance: a max sitting exactly on a step (1,000,000 / 250,000) needs no extra interval.
      const count = Math.ceil(max / step - 1e-9);
      if (count < 3 || count > 5) continue;
      const top = count * step;
      const tie = best !== undefined && Math.abs(top - best.top) <= best.top * 1e-9;
      if (!best || (tie ? count < best.count : top < best.top)) best = { step, count, top };
    }
  }
  if (!best) return undefined;
  const { step, count } = best;
  return Array.from({ length: count + 1 }, (_, i) => Number((i * step).toPrecision(12)));
};

/** Axis label: compact, trailing zeros dropped ("$0", "$400K", "$1.2M", "$1.25M"). */
const formatAxisUsd = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 0 };
const Y_AXIS_WIDTH = 56;
/** Half a month label ("Sep '26" at 12px is ~40px wide), plus a little air. */
const MONTH_LABEL_HALF_WIDTH = 22;

const formatBucket = (key: string, timeframe: VolumeChartProps['timeframe'], long = false) => {
  const [y, m, d] = key.split('-');
  const month = MONTHS[Number(m) - 1] ?? m;
  if (timeframe === 'monthly' || !d) return long ? `${month} ${y}` : `${month} '${y.slice(2)}`;
  if (timeframe === 'weekly') return long ? `Week of ${month} ${Number(d)}, ${y}` : `${month} ${Number(d)}`;
  return long ? `${month} ${Number(d)}, ${y}` : `${month} ${Number(d)}`;
};

/** Bucket key containing "now", built like processVolumeData's keys (UTC day / Sunday week / month). */
const currentBucketKey = (timeframe: VolumeChartProps['timeframe'], now = new Date()) => {
  if (timeframe === 'monthly') return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  if (timeframe === 'weekly') {
    const weekStart = new Date(now);
    weekStart.setUTCDate(now.getUTCDate() - now.getUTCDay());
    return weekStart.toISOString().split('T')[0];
  }
  return now.toISOString().split('T')[0];
};

/** Tooltip note for the current, still incomplete bucket. */
const PARTIAL_LABEL: Record<NonNullable<VolumeChartProps['timeframe']>, string> = {
  monthly: 'Month to date',
  weekly: 'Week to date',
  daily: 'Today',
};

/**
 * Evenly spaced month ticks: every Nth bucket from the first, with N keeping at most
 * `maxLabels`. A tick on the last bucket is skipped when its centred label would
 * overflow the chart's right edge (narrow bars).
 */
const getMonthlyTicks = (keys: string[], maxLabels: number, chartWidth: number) => {
  const step = Math.max(1, Math.ceil(keys.length / maxLabels));
  const ticks = keys.filter((_, i) => i % step === 0);
  const lastBucketTicked = (keys.length - 1) % step === 0;
  if (lastBucketTicked && ticks.length > 1 && chartWidth > 0) {
    const plotWidth = chartWidth - Y_AXIS_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
    const bandWidth = plotWidth / keys.length;
    if (bandWidth / 2 + CHART_MARGIN.right < MONTH_LABEL_HALF_WIDTH) ticks.pop();
  }
  return ticks;
};

const VolumeTooltip: React.FC<any> = ({ active, payload, label, timeframe, partialKey }) => {
  if (!active || !payload?.length) return null;
  const isPartial = partialKey != null && String(label) === partialKey;
  return (
    <ChartTooltipFrame minWidth={170}>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
        {formatBucket(String(label), timeframe, true)}
        {isPartial && ` · ${PARTIAL_LABEL[timeframe as keyof typeof PARTIAL_LABEL]}`}
      </Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{usd(Number(payload[0].value) || 0)}</Typography>
      <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Volume provided</Typography>
    </ChartTooltipFrame>
  );
};

const VolumeChart: React.FC<VolumeChartProps> = ({ data, title, subtitle, timeframe = 'monthly', action }) => {
  const gradientId = `vol-${useId().replace(/:/g, '')}`;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [chartWidth, setChartWidth] = useState(0);

  // Monthly: explicit ticks so labels are evenly spaced (Recharts' own thinning leaves an
  // irregular last gap, e.g. Dec → Mar → Jun → Oct). Daily/weekly keep automatic ticks.
  const monthlyTicks = useMemo(
    () =>
      timeframe === 'monthly' && data.length > 0
        ? getMonthlyTicks(data.map((d) => d.date), isPhone ? 5 : 8, chartWidth)
        : undefined,
    [timeframe, data, isPhone, chartWidth]
  );

  // The last bucket is still filling up when it is the current month / week / day: draw it fainter.
  const lastKey = data[data.length - 1]?.date;
  const partialKey = lastKey != null && lastKey === currentBucketKey(timeframe) ? lastKey : undefined;

  // Explicit round ticks; the domain ends on the last one.
  const yTicks = useMemo(() => volumeTicks(Math.max(0, ...data.map((d) => d.volume || 0))), [data]);

  return (
    <ChartCard title={title} subtitle={subtitle} action={action} height={{ xs: 220, md: 280 }}>
      <ResponsiveContainer width="100%" height="100%" onResize={(width) => setChartWidth(width)}>
        <BarChart data={data} margin={CHART_MARGIN} barCategoryGap="18%">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={tokens.goldLight} stopOpacity={0.95} />
              <stop offset="100%" stopColor={tokens.chart.gold} stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={tokens.chart.grid} />
          <XAxis
            dataKey="date"
            tick={axisTick}
            tickFormatter={(v) => formatBucket(String(v), timeframe)}
            axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
            tickLine={false}
            dy={6}
            ticks={monthlyTicks}
            interval={monthlyTicks ? 0 : 'preserveStartEnd'}
            minTickGap={16}
          />
          <YAxis
            tick={axisTick}
            tickFormatter={(value) => formatAxisUsd(Number(value))}
            axisLine={false}
            tickLine={false}
            width={Y_AXIS_WIDTH}
            ticks={yTicks}
            interval={0}
            domain={yTicks ? [0, yTicks[yTicks.length - 1]] : [0, 'auto']}
          />
          <Tooltip
            content={<VolumeTooltip timeframe={timeframe} partialKey={partialKey} />}
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          />
          <Bar
            dataKey="volume"
            fill={`url(#${gradientId})`}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
            activeBar={{ fill: tokens.goldLight }}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.date} fillOpacity={d.date === partialKey ? 0.4 : 1} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default VolumeChart;
